import { useEffect, useRef } from "react";

import { gsap } from "gsap";

interface SlidePanelProps {
  children: React.ReactNode;
  className?: string;
  panelRef?: React.RefObject<HTMLDivElement>;
  overlayRef?: React.RefObject<HTMLElement>;
  fromX?: string;
  toX?: string;
  duration?: number;
  ease?: string;
  overlayDuration?: number;
}

interface SlideOutPanelOptions {
  panelRef: React.RefObject<HTMLDivElement>;
  overlayRef?: React.RefObject<HTMLElement>;
  panelToX?: string;
  duration?: number;
  ease?: string;
  overlayDuration?: number;
  onComplete?: () => void;
}

export const slideOutPanel = ({
  panelRef,
  overlayRef,
  panelToX = "100%",
  duration = 0.4,
  ease = "power2.in",
  overlayDuration = 0.2,
  onComplete,
}: SlideOutPanelOptions) => {
  if (!panelRef.current) {
    onComplete?.();
    return null;
  }

  const tl = gsap.timeline({ onComplete });

  if (overlayRef?.current) {
    tl.to(overlayRef.current, {
      opacity: 0,
      duration: overlayDuration,
    }).to(
      panelRef.current,
      {
        x: panelToX,
        duration,
        ease,
      },
      `-=${Math.min(overlayDuration, duration)}`,
    );
    return tl;
  }

  tl.to(panelRef.current, {
    x: panelToX,
    duration,
    ease,
  });

  return tl;
};

const SlidePanel = ({
  children,
  className = "",
  panelRef,
  overlayRef,
  fromX = "100%",
  toX = "0%",
  duration = 0.4,
  ease = "power2.out",
  overlayDuration = 0.2,
}: SlidePanelProps) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const activeRef = panelRef ?? internalRef;

  useEffect(() => {
    if (!activeRef.current) return;

    const tl = gsap.timeline();

    tl.fromTo(
      activeRef.current,
      { x: fromX },
      {
        x: toX,
        duration,
        ease,
      },
    );

    if (overlayRef?.current) {
      tl.fromTo(
        overlayRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: overlayDuration,
        },
        "<",
      );
    }

    return () => {
      tl.kill();
    };
  }, [activeRef, overlayRef, fromX, toX, duration, ease, overlayDuration]);

  return (
    <div ref={activeRef} className={className}>
      {children}
    </div>
  );
};

export default SlidePanel;
